import { AnchorHTMLAttributes, FC } from 'react'
import { Link } from 'react-router-dom'

/** Visual variants for the shared button-like link component. */
type ButtonVariant = 'primary' | 'secondary' | 'ghost'
/** Supported size tokens for the shared button-like link component. */
type ButtonSize = 'sm' | 'md'

/** Props for the shared button-like anchor component. */
type ButtonLinkProps = AnchorHTMLAttributes<HTMLAnchorElement> & {
  variant?: ButtonVariant
  size?: ButtonSize
  /** Use for internal routes — renders a React Router Link instead of a plain <a>. */
  to?: string
}

const variantClasses: Record<ButtonVariant, string> = {
  primary: '!text-brand-smoky',
  secondary: '!text-brand-azure',
  ghost: '!text-brand-azure',
}

const variantStyles: Record<ButtonVariant, { ['--btn-bg']: string; ['--btn-hover']: string }> = {
  primary: { '--btn-bg': 'var(--color-gold-primary)', '--btn-hover': 'var(--color-gold-hover)' },
  secondary: { '--btn-bg': 'var(--color-blue-primary)', '--btn-hover': 'var(--color-blue-hover)' },
  ghost: { '--btn-bg': 'var(--color-blue-primary)', '--btn-hover': 'var(--color-blue-hover)' },
}

const textColorStyles: Record<ButtonVariant, string> = {
  primary: 'var(--color-black-primary)',
  secondary: 'var(--color-blue-light)',
  ghost: 'var(--color-blue-light)',
}

const sizeClasses: Record<ButtonSize, string> = {
  sm: 'px-4 py-2 text-sm',
  md: 'px-6 py-3 text-sm',
}

/**
 * Anchor styled as a button for consistent CTA styling.
 * Pass `to` for internal React Router navigation, `href` for external links.
 */
export const ButtonLink: FC<ButtonLinkProps> = ({
  className,
  variant = 'secondary',
  size = 'sm',
  to,
  ...props
}) => {
  const classes = [
    'button-link inline-flex items-center rounded-lg bg-[var(--btn-bg)] font-semibold uppercase tracking-[0.3em] transition-colors duration-150 hover:bg-[var(--btn-hover)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-blue focus-visible:ring-offset-2 focus-visible:ring-offset-brand-azure',
    variantClasses[variant],
    sizeClasses[size],
    className,
  ]
    .filter(Boolean)
    .join(' ')

  const style = { ...variantStyles[variant], color: textColorStyles[variant], ...props.style }

  if (to) {
    return <Link to={to} className={classes} style={style}>{props.children}</Link>
  }

  return (
    <a
      className={classes}
      style={style}
      {...props}
    />
  )
}
